import Phaser from "phaser";

import { DEPTH } from "../config";
import type { FoxyState } from "../types";
import { FONT, textResolution } from "./Avatar";

/** Frames da folha foxy_npc.png (ver tools/genassets/sprites.mjs). */
const FRAME_WALK = [0, 1];
const FRAME_STAND = 2;
const FRAME_SIT = 3;
/** Cadencia das patas andando. */
const WALK_FRAME_MS = 170;

const DIR_LEFT = 1;
const DIR_RIGHT = 2;

const LABEL_DEPTH = DEPTH.overhead + 10;
/**
 * O cracha fica abaixo das patas: a Foxy e baixinha e para colada em quem fez
 * carinho, e em cima ele brigaria com o nome dos jogadores.
 */
const LABEL_OFFSET = 4;
const HEART_KEY = "foxy-heart";

/**
 * A Foxy NPC: quem decide onde ela esta e o servidor (roteiro pelas trilhas,
 * ou seguindo quem fez carinho). Aqui ela so e interpolada ate a ultima
 * posicao recebida, anima pela pose e mostra o cracha com o nome.
 */
export class FoxyNPC {
  private readonly scene: Phaser.Scene;
  private readonly sprite: Phaser.GameObjects.Sprite;
  private readonly label: Phaser.GameObjects.Text;
  private readonly labelBg: Phaser.GameObjects.Graphics;
  private readonly shadow: Phaser.GameObjects.Ellipse;

  private x: number;
  private y: number;
  private targetX: number;
  private targetY: number;
  private pose = "sit";
  /** Ultimo lado (a folha olha para a direita; a esquerda e espelhada). */
  private facingLeft = false;
  /** x do snapshot anterior: o lado sai do movimento horizontal real. */
  private lastStateX: number;
  private hop?: Phaser.Tweens.Tween;
  private lift = 0;

  constructor(scene: Phaser.Scene, state: FoxyState) {
    this.scene = scene;
    this.x = this.targetX = this.lastStateX = state.x;
    this.y = this.targetY = state.y;
    ensureHeartTexture(scene);

    this.shadow = scene.add.ellipse(state.x, state.y, 20, 6, 0x18301e, 0.22);
    this.sprite = scene.add.sprite(state.x, state.y + 1, "foxy-npc", FRAME_SIT).setOrigin(0.5, 1);
    this.label = scene.add
      .text(state.x, state.y + LABEL_OFFSET, "Foxy", {
        fontFamily: FONT,
        fontSize: "9px",
        fontStyle: "bold",
        color: "#ffffff",
        resolution: textResolution(scene.cameras.main.zoom),
      })
      .setOrigin(0.5, 0);
    this.labelBg = scene.add.graphics();
    this.redrawLabelBg();
    this.apply(state);
  }

  /** Onde ela aparece agora (interpolado). */
  get position(): { x: number; y: number } {
    return { x: this.x, y: this.y };
  }

  /**
   * Onde o servidor disse que ela esta (ultimo snapshot). E com esta posicao
   * que o servidor decide se o "E" e carinho: a dica tem de usar a mesma.
   */
  get serverPosition(): { x: number; y: number } {
    return { x: this.targetX, y: this.targetY };
  }

  /** Estado vindo do snapshot. */
  apply(state: FoxyState): void {
    this.targetX = state.x;
    this.targetY = state.y;
    this.pose = state.p;
    // So existem sprites de lado. Andando, o lado vem do deslocamento
    // horizontal entre snapshots, e nao do eixo dominante que o servidor manda:
    // numa diagonal mais vertical (d = cima/baixo) ela ficaria virada para o
    // ultimo lado e pareceria andar de costas. Parada, vale a direcao do
    // servidor (ela vira para quem fez carinho).
    const dx = state.x - this.lastStateX;
    this.lastStateX = state.x;
    if (state.p === "walk") {
      if (Math.abs(dx) > 0.5) this.facingLeft = dx < 0;
    } else if (state.d === DIR_LEFT) this.facingLeft = true;
    else if (state.d === DIR_RIGHT) this.facingLeft = false;
  }

  /** Carinho: coracoes subindo e um pulinho (todo mundo ve). */
  pet(): void {
    for (let n = 0; n < 5; n++) {
      const heart = this.scene.add
        .image(this.x + Phaser.Math.Between(-10, 10), this.y - 20 - this.lift, HEART_KEY)
        .setDepth(LABEL_DEPTH + this.y)
        .setScale(0.6);
      this.scene.tweens.add({
        targets: heart,
        y: heart.y - Phaser.Math.Between(18, 34),
        x: heart.x + Phaser.Math.Between(-8, 8),
        scale: 1,
        alpha: 0,
        delay: n * 90,
        duration: 900,
        ease: "Sine.easeOut",
        onComplete: () => heart.destroy(),
      });
    }
    this.hop?.stop();
    this.hop = this.scene.tweens.add({
      targets: this,
      lift: { from: 0, to: 6 },
      duration: 140,
      yoyo: true,
      repeat: 1,
      ease: "Sine.easeOut",
      onComplete: () => (this.lift = 0),
    });
  }

  applyZoom(zoom: number): void {
    this.label.setResolution(textResolution(zoom));
  }

  update(delta: number): void {
    // mesma interpolacao exponencial dos avatares remotos
    const t = 1 - Math.exp((-delta / 1000) * 10);
    const dx = this.targetX - this.x;
    const dy = this.targetY - this.y;
    if (Math.abs(dx) > 192 || Math.abs(dy) > 192) {
      this.x = this.targetX;
      this.y = this.targetY;
    } else {
      this.x += dx * t;
      this.y += dy * t;
    }

    let frame = FRAME_STAND;
    if (this.pose === "sit") frame = FRAME_SIT;
    else if (this.pose === "walk") frame = FRAME_WALK[Math.floor(this.scene.time.now / WALK_FRAME_MS) % FRAME_WALK.length];
    this.sprite.setFrame(frame).setFlipX(this.facingLeft && frame !== FRAME_SIT);

    const x = Math.round(this.x);
    const y = Math.round(this.y);
    const depth = DEPTH.actors + this.y;
    this.shadow.setPosition(x, y).setDepth(depth - 0.5);
    this.sprite.setPosition(x, y + 1 - this.lift).setDepth(depth);
    this.label.setPosition(x, y + LABEL_OFFSET - this.lift).setDepth(LABEL_DEPTH + y + 2);
    this.labelBg.setPosition(x, y + LABEL_OFFSET - this.lift).setDepth(LABEL_DEPTH + y + 1);
  }

  destroy(): void {
    this.hop?.stop();
    this.sprite.destroy();
    this.shadow.destroy();
    this.label.destroy();
    this.labelBg.destroy();
  }

  /** Cracha laranja da marca atras do texto, centrado em (0, 0) = topo do texto. */
  private redrawLabelBg(): void {
    const w = Math.ceil(this.label.width) + 8;
    const h = Math.ceil(this.label.height) + 1;
    this.labelBg.clear();
    this.labelBg.fillStyle(0xf0811f, 0.92);
    this.labelBg.fillRoundedRect(-w / 2, -1, w, h, 5);
  }
}

/** Coracao de pixel art (laranja da marca), gerado uma vez por cena. */
function ensureHeartTexture(scene: Phaser.Scene): void {
  if (scene.textures.exists(HEART_KEY)) return;
  const rows = [".##.##.", "#######", "#######", ".#####.", "..###..", "...#..."];
  const g = scene.add.graphics();
  rows.forEach((row, y) =>
    [...row].forEach((ch, x) => {
      if (ch !== "#") return;
      g.fillStyle(y === 0 || (y === 1 && x < 2) ? 0xffb066 : 0xf0811f, 1);
      g.fillRect(x, y, 1, 1);
    }),
  );
  g.generateTexture(HEART_KEY, 7, 6);
  g.destroy();
}
